import { useCallback, useEffect, useRef, useState, memo, useMemo } from "react";
import type { MessageDescriptor } from "@lingui/core";

import { Trans, msg } from "@lingui/macro";
import { useLingui } from "@lingui/react";

import { Button } from "@/components/ui/button";
import { ErrorCallout } from "@/components/ui/error-callout";
import { patientIntakeApi, buildFormPayload } from "@/lib/patientIntakeApi";
import { repairIntakePhoneFields } from "@/lib/phoneNumber";
import type { IntakeData } from "@/lib/types";
import { intakeStringPreserveWhitespaceWhileTyping } from "@/lib/intakeStringSanitize";
import {
  ClipboardCheck,
  UserCheck,
  FileText,
  CheckCircle,
  Upload,
  Shield,
  ChevronLeft,
  ChevronRight,
  Camera,
  Brain,
  HelpCircle,
  type LucideIcon,
} from "lucide-react";

import { useSearchParams } from "react-router-dom";
import {
  captureReferralCode,
  trackReferralStarted,
  trackReferralCompleted,
} from "@/lib/referralTracker";

// Import simplified step components
import WelcomeStep from "../components/intake/WelcomeStep";
import MedicalProcedureStep from "../components/intake/MedicalProcedureStep";
import DetailedQuestionsStep from "../components/intake/DetailedQuestionsStep";
import UploadsStep from "../components/intake/UploadsStep";
import ReviewConsentStep from "../components/intake/ReviewConsentStep";
import { finalizeWithConsents } from "@/lib/buildConsents";
import VerifyEmailStep from "../components/intake/VerifyEmailStep";
import SuccessStep from "../components/intake/SuccessStep";
import DynamicQuestionTree from "../components/intake/dentist/DynamicQuestionTree";
import PhotoCaptureStep from "../components/intake/dentist/PhotoCaptureStep";
import AdditionalPhotosStep from "../components/intake/dentist/AdditionalPhotosStep";
import ComprehensiveDentalAnalysis from "../components/intake/dentist/ComprehensiveDentalAnalysis";
import ReviewStep from "../components/intake/dentist/ReviewStep";
import OTPVerificationStep from "../components/intake/dentist/OTPVerificationStep";
import SinglePhotoRetake from "../components/intake/dentist/SinglePhotoRetake";

const MemoizedDynamicQuestionTree = memo(DynamicQuestionTree);
const MemoizedPhotoCaptureStep = memo(PhotoCaptureStep);
const MemoizedAdditionalPhotosStep = memo(AdditionalPhotosStep);
const MemoizedComprehensiveDentalAnalysis = memo(ComprehensiveDentalAnalysis);
const MemoizedReviewStep = memo(ReviewStep);
const MemoizedOTPVerificationStep = memo(OTPVerificationStep);
const MemoizedSuccessStep = memo(SuccessStep);

const STEP_SUBHEADINGS = [
  msg`Let's get started with your information`,
  msg`Tell us about your medical needs`,
  msg`Upload your documents`,
  msg`Review and submit your intake`,
  msg`Verify your email address`,
  msg`All done! Welcome to Jema Services`,
];

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const normalizeEmail = (email?: string) => (email || "").trim().toLowerCase();
const isLocalMediaUrl = (value: unknown): value is string =>
  typeof value === "string" &&
  (value.startsWith("data:") || value.startsWith("blob:"));

const INTAKE_HISTORY_KIND = "jema-intake" as const;

const isDesktopViewport = () =>
  typeof window !== "undefined" && window.innerWidth >= 768;

const stripServerMediaFromUploads = (
  uploads: IntakeData["uploads"],
): IntakeData["uploads"] => {
  const categories: Array<keyof IntakeData["uploads"]> = [
    "idPassport",
    "labsImaging",
    "photos",
  ];
  const cleaned: IntakeData["uploads"] = {};

  categories.forEach((category) => {
    const items = uploads?.[category];
    if (!Array.isArray(items)) {
      return;
    }

    const localFiles = items.filter((item): item is File => item instanceof File);
    if (localFiles.length > 0) {
      cleaned[category] = localFiles;
    }
  });

  return cleaned;
};

const buildEmailChangeReset = (data: IntakeData): Partial<IntakeData> => {
  const localPhotos = Object.fromEntries(
    Object.entries(data.photos || {}).filter(([, value]) => isLocalMediaUrl(value)),
  );
  const localAdditionalPhotos = (data.additionalPhotos || []).filter((value) =>
    isLocalMediaUrl(value),
  );

  return {
    formUid: "",
    referenceUid: "",
    emailVerified: false,
    uploads: stripServerMediaFromUploads(data.uploads || {}),
    documentReferenceLinks: [],
    photoMediaMap: {},
    additionalPhotoMediaIds: [],
    photos: localPhotos,
    additionalPhotos: localAdditionalPhotos,
  };
};

// eslint-disable-next-line react-refresh/only-export-components -- exported for unit tests
export const sanitizeIntakeUpdates = (updates: Partial<IntakeData>) => {
  const sanitized = {} as Record<
    keyof IntakeData,
    IntakeData[keyof IntakeData]
  >;
  (
    Object.entries(updates) as Array<
      [keyof IntakeData, IntakeData[keyof IntakeData]]
    >
  ).forEach(([key, value]) => {
    if (typeof value === "string") {
      if (key === "email") {
        sanitized[key] = value.trim().toLowerCase() as IntakeData[keyof IntakeData];
        return;
      }
      if (intakeStringPreserveWhitespaceWhileTyping(key)) {
        sanitized[key] = value as IntakeData[keyof IntakeData];
        return;
      }
      const trimmed = value.trim();
      sanitized[key] = trimmed as IntakeData[keyof IntakeData];
      return;
    }
    if (Array.isArray(value)) {
      sanitized[key] = value as IntakeData[keyof IntakeData];
      return;
    }
    if (value !== undefined) {
      sanitized[key] = value;
    }
  });

  return sanitized as Partial<IntakeData>;
};

const hasValidIdentity = (data: IntakeData) => {
  const firstName = data.firstName?.trim();
  const lastName = data.lastName?.trim();
  const email = data.email?.trim();
  const phone = data.phone?.trim();
  const country = data.country?.trim();

  return (
    Boolean(firstName) &&
    Boolean(lastName) &&
    Boolean(phone) &&
    Boolean(country) &&
    Boolean(email && emailRegex.test(email))
  );
};

// Parse URL parameters and return prefilled form data
const parseUrlParams = (searchParams: URLSearchParams): Partial<IntakeData> => {
  const prefilled: Partial<IntakeData> = {};

  // Helper to get and decode a parameter
  const getParam = (key: string): string | undefined => {
    const value = searchParams.get(key);
    return value ? decodeURIComponent(value) : undefined;
  };

  // Helper to get array parameter (comma-separated)
  const getArrayParam = (key: string): string[] | undefined => {
    const value = getParam(key);
    if (!value) return undefined;
    return value
      .split(",")
      .map((v) => v.trim())
      .filter((v) => v.length > 0);
  };

  // Step 1: Identity Information
  if (getParam("firstName")) prefilled.firstName = getParam("firstName")!;
  if (getParam("lastName")) prefilled.lastName = getParam("lastName")!;
  if (getParam("email")) prefilled.email = getParam("email")!;
  if (getParam("phone")) prefilled.phone = getParam("phone")!;
  if (getParam("country")) prefilled.country = getParam("country")!;
  if (getParam("preferredLanguage")) {
    const lang = getParam("preferredLanguage")!;
    if (lang === "en" || lang === "es") {
      prefilled.preferredLanguage = lang;
    }
  }

  // Step 2: Physical & Medical Information
  if (getParam("dateOfBirth")) prefilled.dateOfBirth = getParam("dateOfBirth")!;
  if (getParam("height")) prefilled.height = getParam("height")!;
  if (getParam("weight")) prefilled.weight = getParam("weight")!;
  if (getParam("city")) prefilled.city = getParam("city")!;
  if (getParam("state")) prefilled.state = getParam("state")!;
  if (getParam("procedures")) prefilled.procedures = getParam("procedures")!;

  // Travel Information
  if (getParam("plannedTravelMonth"))
    prefilled.plannedTravelMonth = getParam("plannedTravelMonth")!;
  if (getParam("travelingWith"))
    prefilled.travelingWith = getParam("travelingWith")!;

  // Health Background
  const conditions = getArrayParam("currentMedicalConditions");
  if (conditions) prefilled.currentMedicalConditions = conditions;
  if (getParam("otherCondition"))
    prefilled.otherCondition = getParam("otherCondition")!;
  if (getParam("pastSurgeries"))
    prefilled.pastSurgeries = getParam("pastSurgeries")!;
  if (getParam("allergiesMedications"))
    prefilled.allergiesMedications = getParam("allergiesMedications")!;
  if (getParam("preExistingConditions"))
    prefilled.preExistingConditions = getParam("preExistingConditions")!;
  if (getParam("currentMedications"))
    prefilled.currentMedications = getParam("currentMedications")!;
  if (getParam("allergies")) prefilled.allergies = getParam("allergies")!;
  if (getParam("majorSurgeries"))
    prefilled.majorSurgeries = getParam("majorSurgeries")!;

  // Lifestyle Information
  if (getParam("smokingHabits"))
    prefilled.smokingHabits = getParam("smokingHabits")!;
  if (getParam("alcoholUse")) prefilled.alcoholUse = getParam("alcoholUse")!;
  if (getParam("alcoholConsumption"))
    prefilled.alcoholConsumption = getParam("alcoholConsumption")!;
  if (getParam("exerciseFrequency"))
    prefilled.exerciseFrequency = getParam("exerciseFrequency")!;

  // Additional Medical Questions
  if (getParam("mobilityIssues"))
    prefilled.mobilityIssues = getParam("mobilityIssues")!;
  if (getParam("dietaryRestrictions"))
    prefilled.dietaryRestrictions = getParam("dietaryRestrictions")!;
  if (getParam("travelConcerns"))
    prefilled.travelConcerns = getParam("travelConcerns")!;
  if (getParam("travelAlone")) prefilled.travelAlone = getParam("travelAlone")!;
  if (getParam("budgetExpectations"))
    prefilled.budgetExpectations = getParam("budgetExpectations")!;
  if (getParam("personalInterest"))
    prefilled.personalInterest = getParam("personalInterest")!;

  // Vascular Specific Fields
  const vascularPrimaryGoal = getArrayParam("vascularPrimaryGoal");
  if (vascularPrimaryGoal) prefilled.vascularPrimaryGoal = vascularPrimaryGoal;
  if (getParam("vascularSymptoms"))
    prefilled.vascularSymptoms = getParam("vascularSymptoms")!;
  const vascularSymptomTypes = getArrayParam("vascularSymptomTypes");
  if (vascularSymptomTypes)
    prefilled.vascularSymptomTypes = vascularSymptomTypes;
  if (getParam("vascularDiagnosisStatus"))
    prefilled.vascularDiagnosisStatus = getParam("vascularDiagnosisStatus")!;
  if (getParam("vascularUrgency"))
    prefilled.vascularUrgency = getParam("vascularUrgency")!;
  if (getParam("vascularProceduralExpectation"))
    prefilled.vascularProceduralExpectation = getParam(
      "vascularProceduralExpectation",
    )!;

  // Hair Transplant Specific Fields
  const hairTransplantTreatmentAreas = getArrayParam(
    "hairTransplantTreatmentAreas",
  );
  if (hairTransplantTreatmentAreas)
    prefilled.hairTransplantTreatmentAreas = hairTransplantTreatmentAreas;
  if (getParam("hairTransplantTreatmentAreasOther"))
    prefilled.hairTransplantTreatmentAreasOther = getParam(
      "hairTransplantTreatmentAreasOther",
    )!;
  if (getParam("hairTransplantProgression"))
    prefilled.hairTransplantProgression = getParam(
      "hairTransplantProgression",
    )!;
  const hairTransplantPreviousTreatments = getArrayParam(
    "hairTransplantPreviousTreatments",
  );
  if (hairTransplantPreviousTreatments)
    prefilled.hairTransplantPreviousTreatments =
      hairTransplantPreviousTreatments;
  if (getParam("hairTransplantOutcomeExpectation"))
    prefilled.hairTransplantOutcomeExpectation = getParam(
      "hairTransplantOutcomeExpectation",
    )!;
  if (getParam("hairTransplantTimeline"))
    prefilled.hairTransplantTimeline = getParam("hairTransplantTimeline")!;

  // Optical Specific Fields
  const opticalPrimaryReason = getArrayParam("opticalPrimaryReason");
  if (opticalPrimaryReason) prefilled.opticalPrimaryReason = opticalPrimaryReason;
  if (getParam("opticalVisionCorrection"))
    prefilled.opticalVisionCorrection = getParam("opticalVisionCorrection")!;
  if (getParam("opticalLaterality"))
    prefilled.opticalLaterality = getParam("opticalLaterality")!;
  const opticalSymptoms = getArrayParam("opticalSymptoms");
  if (opticalSymptoms) prefilled.opticalSymptoms = opticalSymptoms;
  if (getParam("opticalProcedureInterest"))
    prefilled.opticalProcedureInterest = getParam("opticalProcedureInterest")!;

  // Plastic Surgery Specific Fields
  if (getParam("plasticAreaOfInterest"))
    prefilled.plasticAreaOfInterest = getParam("plasticAreaOfInterest")!;
  if (getParam("plasticProcedureType"))
    prefilled.plasticProcedureType = getParam("plasticProcedureType")!;
  if (getParam("plasticSpecificConcern"))
    prefilled.plasticSpecificConcern = getParam("plasticSpecificConcern")!;
  if (getParam("plasticPreviousSurgery"))
    prefilled.plasticPreviousSurgery = getParam("plasticPreviousSurgery")!;
  if (getParam("plasticOutcomePriority"))
    prefilled.plasticOutcomePriority = getParam("plasticOutcomePriority")!;

  // Diagnostics/Imaging Specific Fields
  const diagnosticsPurpose = getArrayParam("diagnosticsPurpose");
  if (diagnosticsPurpose) prefilled.diagnosticsPurpose = diagnosticsPurpose;
  if (getParam("diagnosticsType"))
    prefilled.diagnosticsType = getParam("diagnosticsType")!;
  if (getParam("diagnosticsArea"))
    prefilled.diagnosticsArea = getParam("diagnosticsArea")!;
  if (getParam("diagnosticsTimeSensitivity"))
    prefilled.diagnosticsTimeSensitivity = getParam(
      "diagnosticsTimeSensitivity",
    )!;
  const diagnosticsCategory = getParam("diagnosticsCategory");
  if (
    diagnosticsCategory === "imaging" ||
    diagnosticsCategory === "diagnostics"
  ) {
    prefilled.diagnosticsCategory = diagnosticsCategory;
  }
  if (getParam("diagnosticsReason"))
    prefilled.diagnosticsReason = getParam("diagnosticsReason")!;
  if (getParam("diagnosticsReasonOther"))
    prefilled.diagnosticsReasonOther = getParam("diagnosticsReasonOther")!;
  if (getParam("diagnosticsHasSymptoms"))
    prefilled.diagnosticsHasSymptoms = getParam("diagnosticsHasSymptoms")!;
  const diagnosticsSymptoms = getArrayParam("diagnosticsSymptoms");
  if (diagnosticsSymptoms) prefilled.diagnosticsSymptoms = diagnosticsSymptoms;
  if (getParam("diagnosticsSymptomOther"))
    prefilled.diagnosticsSymptomOther = getParam("diagnosticsSymptomOther")!;
  const diagnosticsConditions = getArrayParam("diagnosticsConditions");
  if (diagnosticsConditions)
    prefilled.diagnosticsConditions = diagnosticsConditions;
  if (getParam("diagnosticsConditionOther"))
    prefilled.diagnosticsConditionOther = getParam(
      "diagnosticsConditionOther",
    )!;
  if (getParam("diagnosticsDoctorStatus"))
    prefilled.diagnosticsDoctorStatus = getParam("diagnosticsDoctorStatus")!;
  if (getParam("diagnosticsDoctorList"))
    prefilled.diagnosticsDoctorList = getParam("diagnosticsDoctorList")!;
  if (getParam("diagnosticsScope"))
    prefilled.diagnosticsScope = getParam("diagnosticsScope")!;
  if (getParam("diagnosticsRecentTesting"))
    prefilled.diagnosticsRecentTesting = getParam("diagnosticsRecentTesting")!;
  if (getParam("diagnosticsShareRecentResults"))
    prefilled.diagnosticsShareRecentResults = getParam(
      "diagnosticsShareRecentResults",
    )!;
  if (getParam("diagnosticsLabUrgency"))
    prefilled.diagnosticsLabUrgency = getParam("diagnosticsLabUrgency")!;
  if (getParam("diagnosticsFasting"))
    prefilled.diagnosticsFasting = getParam("diagnosticsFasting")!;
  if (getParam("diagnosticsResultsUsage"))
    prefilled.diagnosticsResultsUsage = getParam("diagnosticsResultsUsage")!;
  if (getParam("diagnosticsFollowUp"))
    prefilled.diagnosticsFollowUp = getParam("diagnosticsFollowUp")!;

  // Fertility Specific Fields
  const fertilityCareFocus = getArrayParam("fertilityCareFocus");
  if (fertilityCareFocus) prefilled.fertilityCareFocus = fertilityCareFocus;
  if (getParam("fertilityPatientType"))
    prefilled.fertilityPatientType = getParam("fertilityPatientType")!;
  if (getParam("fertilityPreviousAttempts"))
    prefilled.fertilityPreviousAttempts = getParam(
      "fertilityPreviousAttempts",
    )!;
  if (getParam("fertilityKnownDiagnosis"))
    prefilled.fertilityKnownDiagnosis = getParam("fertilityKnownDiagnosis")!;
  if (getParam("fertilityTimeline"))
    prefilled.fertilityTimeline = getParam("fertilityTimeline")!;

  // Stem Cell Specific Fields
  const stemCellReason = getArrayParam("stemCellReason");
  if (stemCellReason) prefilled.stemCellReason = stemCellReason;
  if (getParam("stemCellReasonOther"))
    prefilled.stemCellReasonOther = getParam("stemCellReasonOther")!;
  if (getParam("stemCellLocation"))
    prefilled.stemCellLocation = getParam("stemCellLocation")!;
  if (getParam("stemCellDiagnosisStatus"))
    prefilled.stemCellDiagnosisStatus = getParam("stemCellDiagnosisStatus")!;
  if (getParam("stemCellTreatmentGoal"))
    prefilled.stemCellTreatmentGoal = getParam("stemCellTreatmentGoal")!;
  const stemCellPriorTreatments = getArrayParam("stemCellPriorTreatments");
  if (stemCellPriorTreatments)
    prefilled.stemCellPriorTreatments = stemCellPriorTreatments;

  // Dental Specific Fields
  const dentalProcedureTypeParam = getArrayParam("dentalProcedureType");
  if (dentalProcedureTypeParam)
    prefilled.dentalProcedureType = dentalProcedureTypeParam;
  if (getParam("dentalDiscomfort"))
    prefilled.dentalDiscomfort = getParam("dentalDiscomfort")!;

  return prefilled;
};

export default function Index() {
  const { i18n } = useLingui();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    captureReferralCode();
    void trackReferralStarted();
  }, []);

  const [currentStep, setCurrentStep] = useState(0);
  const currentStepRef = useRef(0);
  currentStepRef.current = currentStep;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [trustedSessionStatus, setTrustedSessionStatus] = useState<
    "idle" | "loading" | "ready" | "error"
  >("idle");
  const contentRef = useRef<HTMLElement | null>(null);
  const didMountRef = useRef(false);
  const stepRefs = useRef<(HTMLDivElement | null)[]>([]);
  const trustedBootstrapRef = useRef(false);
  const mobileHistorySeededRef = useRef(false);

  // Parse URL parameters for prefilling
  const urlPrefilledData = useMemo(
    () => parseUrlParams(searchParams),
    [searchParams],
  );

  const [intakeData, setIntakeData] = useState<IntakeData>(() => {
    const merged: IntakeData = {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      country: "",
      preferredLanguage: "en",
      procedures: "",
      dateOfBirth: "",
      preExistingConditions: "",
      currentMedications: "",
      allergies: "",
      majorSurgeries: "",
      smokingHabits: "",
      dentalProcedureType: [],
      dentalDiscomfort: "",
      uploads: {},
      documentReferenceLinks: [],
      consentGiven: false,
      emailVerified: false,
      ...urlPrefilledData,
    };
    const phoneCountry =
      merged.phoneCountry?.trim() || merged.country?.trim() || "US";
    const repaired = repairIntakePhoneFields(merged.phone, phoneCountry);
    return {
      ...merged,
      phone: repaired.phone,
      phoneCountry: merged.phoneCountry || repaired.country,
      country: merged.country || repaired.country,
    };
  });

  const STEPS = useMemo<
    {
      id: string;
      hash?: string;
      title: MessageDescriptor;
      icon: LucideIcon;
    }[]
  >(
    () => [
      {
        id: "welcome",
        hash: "welcome-step",
        title: msg`Personal Info`,
        icon: UserCheck,
      },
      {
        id: "medical",
        hash: "medical-step",
        title: msg`Medical Info`,
        icon: FileText,
      },
      ...(intakeData.procedures === "dentistry"
        ? [
            {
              id: "detailed",
              hash: "detailed",
              title: msg`Detailed Questions`,
              icon: HelpCircle,
            },
            {
              id: "photos",
              title: msg`Your Photos`,
              hash: "your-photos",
              icon: Camera,
            },
            {
              id: "additional-photos",
              title: msg`Additional Photos`,
              hash: "additional-photos",
              icon: Upload,
            },
            {
              id: "analysis",
              title: msg`Full Analysis`,
              hash: "full-analysis",
              icon: Brain,
            },
            {
              id: "review",
              title: msg`Review & Submit`,
              icon: ClipboardCheck,
              hash: "review-submit",
            },
            {
              id: "verification",
              title: msg`Email Verification`,
              icon: Shield,
              hash: "email-verification",
            },
            {
              id: "success",
              title: msg`All Done!`,
              icon: CheckCircle,
              hash: "all-done",
            },
          ]
        : [
            {
              id: "detailed",
              hash: "detailed",
              title: msg`Detailed Questions`,
              icon: HelpCircle,
            },
            {
              id: "uploads",
              hash: "uploads-step",
              title: msg`Upload Documents`,
              icon: Upload,
            },
            {
              id: "review",
              hash: "review-step",
              title: msg`Review & Submit`,
              icon: ClipboardCheck,
            },
            {
              id: "verification",
              hash: "verification-step",
              title: msg`Email Verification`,
              icon: Shield,
            },
            {
              id: "success",
              hash: "success",
              title: msg`All Done!`,
              icon: CheckCircle,
            },
          ]),
    ].filter(
      (step) =>
        !(
          trustedSessionStatus === "ready" &&
          step.id === "verification"
        ),
    ),
    [intakeData.procedures, trustedSessionStatus],
  );

  const stepsRef = useRef(STEPS);
  stepsRef.current = STEPS;

  const intakeDataRef = useRef<IntakeData>(intakeData);
  const draftSyncTimeoutRef = useRef<number | null>(null);
  const draftSyncPromiseRef = useRef<Promise<void> | null>(null);
  const lastSyncedSignatureRef = useRef<string | null>(null);
  const draftEmailRef = useRef<string | null>(null);
  const draftSyncGenerationRef = useRef(0);
  const [stepError, setStepError] = useState<{
    message: string;
    status?: number | null;
  } | null>(null);

  const [isDesktop, setIsDesktop] = useState<boolean>(() => {
    if (typeof window === "undefined") {
      return false;
    }
    return window.innerWidth >= 768;
  });

  useEffect(() => {
    intakeDataRef.current = intakeData;
  }, [intakeData]);

  useEffect(() => {
    if (!intakeData.formUid) {
      draftEmailRef.current = null;
      return;
    }

    if (!draftEmailRef.current) {
      const normalizedEmail = normalizeEmail(intakeData.email);
      if (normalizedEmail) {
        draftEmailRef.current = normalizedEmail;
      }
    }
  }, [intakeData.formUid, intakeData.email]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const pushMobileIntakeHistory = useCallback((stepIndex: number) => {
    if (typeof window === "undefined" || isDesktopViewport()) return;
    const step = stepsRef.current[stepIndex];
    if (!step) return;
    const hashValue = step.hash ?? step.id ?? "";
    const desiredHash = hashValue ? `#${hashValue}` : "";
    const { pathname, search } = window.location;
    const prev = window.history.state;
    const base =
      prev && typeof prev === "object" && !Array.isArray(prev)
        ? { ...(prev as Record<string, unknown>) }
        : {};
    window.history.pushState(
      {
        ...base,
        intakeStep: stepIndex,
        intakeKind: INTAKE_HISTORY_KIND,
      },
      "",
      `${pathname}${search}${desiredHash}`,
    );
  }, []);

  const replaceMobileIntakeHistory = useCallback((stepIndex: number) => {
    if (typeof window === "undefined" || isDesktopViewport()) return;
    const step = stepsRef.current[stepIndex];
    if (!step) return;
    const hashValue = step.hash ?? step.id ?? "";
    const desiredHash = hashValue ? `#${hashValue}` : "";
    const { pathname, search } = window.location;
    const prev = window.history.state;
    const base =
      prev && typeof prev === "object" && !Array.isArray(prev)
        ? { ...(prev as Record<string, unknown>) }
        : {};
    window.history.replaceState(
      {
        ...base,
        intakeStep: stepIndex,
        intakeKind: INTAKE_HISTORY_KIND,
      },
      "",
      `${pathname}${search}${desiredHash}`,
    );
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || isDesktop) return;
    if (mobileHistorySeededRef.current) return;
    mobileHistorySeededRef.current = true;

    const step0 = stepsRef.current[0];
    if (!step0) return;
    const hashValue = step0.hash ?? step0.id ?? "";
    const desiredHash = hashValue ? `#${hashValue}` : "";
    const { pathname, search } = window.location;
    const prev = window.history.state;
    const base =
      prev && typeof prev === "object" && !Array.isArray(prev)
        ? { ...(prev as Record<string, unknown>) }
        : {};
    if (
      (base as { intakeKind?: string }).intakeKind === INTAKE_HISTORY_KIND &&
      typeof (base as { intakeStep?: number }).intakeStep === "number"
    ) {
      return;
    }
    window.history.replaceState(
      {
        ...base,
        intakeStep: 0,
        intakeKind: INTAKE_HISTORY_KIND,
      },
      "",
      `${pathname}${search}${desiredHash}`,
    );
  }, [isDesktop, STEPS]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const onPopState = () => {
      if (isDesktopViewport()) return;
      const st = window.history.state as {
        intakeKind?: string;
        intakeStep?: number;
      } | null;
      if (
        st?.intakeKind !== INTAKE_HISTORY_KIND ||
        typeof st.intakeStep !== "number"
      ) {
        return;
      }
      const idx = st.intakeStep;
      const len = stepsRef.current.length;
      if (idx >= 0 && idx < len) {
        setCurrentStep(idx);
      }
    };

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const scrollToTop = useCallback((behavior: ScrollBehavior = "smooth") => {
    const scrollContainers = () => {
      const container = contentRef.current;
      if (container) {
        if (typeof container.scrollTo === "function") {
          container.scrollTo({ top: 0, left: 0, behavior });
        } else {
          container.scrollTop = 0;
          container.scrollLeft = 0;
        }
      }
      if (typeof window !== "undefined") {
        window.scrollTo({ top: 0, left: 0, behavior });
        window.scrollTo(0, 0);
        const globalTargets: Array<HTMLElement | null> = [
          (document.scrollingElement as HTMLElement | null) ?? null,
          document.documentElement,
          document.body,
        ];
        globalTargets.forEach((target) => {
          if (!target) {
            return;
          }
          if (typeof target.scrollTo === "function") {
            try {
              target.scrollTo({ top: 0, left: 0, behavior });
            } catch {
              target.scrollTop = 0;
              target.scrollLeft = 0;
            }
          }
          target.scrollTop = 0;
          target.scrollLeft = 0;
        });
      }

      const firstChild = container?.firstElementChild as HTMLElement | null;
      if (firstChild?.scrollIntoView) {
        firstChild.scrollIntoView({ behavior, block: "start" });
      }
    };

    scrollContainers();

    if (typeof window !== "undefined") {
      window.requestAnimationFrame(() => {
        scrollContainers();
      });
      window.setTimeout(() => {
        scrollContainers();
      }, 150);
    }
  }, []);

  // Removed hash listener to force start at step 0 on reload
  // useEffect(() => { ... }, [STEPS]);

  useEffect(() => {
    if (typeof window === "undefined" || !isDesktop) {
      return;
    }

    const step = STEPS[currentStep];
    if (!step) {
      return;
    }

    const hashValue = step.hash ?? step.id ?? "";
    const desiredHash = hashValue ? `#${hashValue}` : "";

    if (window.location.hash === desiredHash) {
      return;
    }

    const { pathname, search } = window.location;
    window.history.replaceState(null, "", `${pathname}${search}${desiredHash}`);
  }, [currentStep, STEPS, isDesktop]);

  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    scrollToTop();
  }, [currentStep, scrollToTop]);

  // Handle step array changes when procedures change
  useEffect(() => {
    // Ensure currentStep is within bounds when STEPS array changes
    if (currentStep >= STEPS.length) {
      setCurrentStep(Math.max(0, STEPS.length - 1));
    }
  }, [STEPS.length, currentStep]);

  useEffect(() => {
    stepRefs.current[currentStep]?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });
  }, [currentStep]);

  const performDraftSync = useCallback(async () => {
    const syncGeneration = draftSyncGenerationRef.current;
    const initial = intakeDataRef.current;
    if (!initial.formUid) {
      return;
    }

    if (draftSyncPromiseRef.current) {
      try {
        await draftSyncPromiseRef.current;
      } catch {
        // Previous sync errors are already logged
      }
    }

    const latest = intakeDataRef.current;
    if (syncGeneration !== draftSyncGenerationRef.current || !latest.formUid) {
      return;
    }

    if (!hasValidIdentity(latest)) {
      return;
    }

    const payload = buildFormPayload(latest);
    const signature = JSON.stringify(payload);
    if (lastSyncedSignatureRef.current === signature) {
      return;
    }

    const formUid = latest.formUid;
    const syncPromise = (async () => {
      try {
        if (syncGeneration !== draftSyncGenerationRef.current) {
          return;
        }
        await patientIntakeApi.updateIntake(formUid, latest);
        if (syncGeneration === draftSyncGenerationRef.current) {
          lastSyncedSignatureRef.current = signature;
        }
      } catch (error) {
        console.error("Failed to sync intake draft", error);
      }
    })();

    draftSyncPromiseRef.current = syncPromise;

    try {
      await syncPromise;
    } finally {
      if (draftSyncPromiseRef.current === syncPromise) {
        draftSyncPromiseRef.current = null;
      }
    }
  }, []);

  const scheduleDraftSync = useCallback(() => {
    if (typeof window === "undefined") {
      return;
    }

    if (!intakeDataRef.current.formUid) {
      return;
    }

    if (draftSyncTimeoutRef.current) {
      window.clearTimeout(draftSyncTimeoutRef.current);
    }

    draftSyncTimeoutRef.current = window.setTimeout(() => {
      draftSyncTimeoutRef.current = null;
      void performDraftSync();
    }, 800);
  }, [performDraftSync]);

  const flushDraftSync = useCallback(async () => {
    if (typeof window !== "undefined" && draftSyncTimeoutRef.current) {
      window.clearTimeout(draftSyncTimeoutRef.current);
      draftSyncTimeoutRef.current = null;
    }

    await performDraftSync();

    if (draftSyncPromiseRef.current) {
      try {
        await draftSyncPromiseRef.current;
      } catch {
        // Error already logged during performDraftSync
      }
    }
  }, [performDraftSync]);

  const clearDraftSyncTimeout = useCallback((invalidateInFlight = false) => {
    if (typeof window !== "undefined" && draftSyncTimeoutRef.current) {
      window.clearTimeout(draftSyncTimeoutRef.current);
      draftSyncTimeoutRef.current = null;
    }
    if (invalidateInFlight) {
      draftSyncGenerationRef.current += 1;
    }
  }, []);

  useEffect(() => {
    return () => {
      if (typeof window !== "undefined" && draftSyncTimeoutRef.current) {
        window.clearTimeout(draftSyncTimeoutRef.current);
        draftSyncTimeoutRef.current = null;
      }
      void flushDraftSync();
    };
  }, [flushDraftSync]);

  const updateIntakeData = useCallback(
    (data: Partial<IntakeData>) => {
      const sanitized = sanitizeIntakeUpdates(data);
      console.log("📝 updateIntakeData called with:", {
        procedures: sanitized.procedures,
        uploads: sanitized.uploads,
        hasUploads: !!sanitized.uploads,
      });

      setIntakeData((prev) => {
        console.log("📝 Previous state:", {
          procedures: prev.procedures,
          uploads: prev.uploads,
        });

        // If procedures field is being updated and it's different from current, clear uploads
        if (
          sanitized.procedures &&
          sanitized.procedures !== prev.procedures &&
          prev.procedures !== ""
        ) {
          console.log(
            "🔄 Procedure changed in updateIntakeData, clearing uploads",
            {
              from: prev.procedures,
              to: sanitized.procedures,
            },
          );
          sanitized.uploads = {};
        }

        const next = { ...prev, ...sanitized };
        console.log("📝 New state:", {
          procedures: next.procedures,
          uploads: next.uploads,
        });

        intakeDataRef.current = next;
        if (next.formUid) {
          scheduleDraftSync();
        }
        return next;
      });
    },
    [setIntakeData, scheduleDraftSync],
  );

  const verifiedParam = searchParams.get("verified");
  const queryFormUid = searchParams.get("formUid");
  const queryReferenceUid = searchParams.get("referenceUid");
  const trustedToken = searchParams.get("token");
  const trustedSource = searchParams.get("source") || undefined;

  useEffect(() => {
    const urlFormUid = searchParams.get("formUid") || undefined;

    if (!urlFormUid || !trustedToken || trustedBootstrapRef.current) {
      return;
    }

    trustedBootstrapRef.current = true;

    let cancelled = false;

    (async () => {
      setTrustedSessionStatus("loading");

      try {
        const response = await patientIntakeApi.startTrustedSession({
          formUid: urlFormUid,
          token: trustedToken,
          ...(trustedSource ? { source: trustedSource } : {}),
        });

        if (cancelled) {
          return;
        }

        const data = response?.data ?? response;

        if (data?.accessToken) {
          try {
            localStorage.setItem("accessToken", data.accessToken);
            if (data.refreshToken) {
              localStorage.setItem("refreshToken", data.refreshToken);
            }
          } catch {
            // Ignore storage errors (e.g. private mode)
          }
        }

        const intakeForm = (data?.intakeForm || {}) as {
          formUid?: string;
          referenceUid?: string;
          identity?: {
            firstName?: string;
            lastName?: string;
            email?: string;
            phone?: string;
            country?: string;
            preferredLanguage?: string;
          };
        };

        const identityUpdates: Partial<IntakeData> = {};

        if (intakeForm.formUid) {
          identityUpdates.formUid = intakeForm.formUid;
        } else if (urlFormUid) {
          identityUpdates.formUid = urlFormUid;
        }

        if (intakeForm.referenceUid) {
          identityUpdates.referenceUid = intakeForm.referenceUid;
        }

        if (intakeForm.identity) {
          const identity = intakeForm.identity;
          if (identity.firstName) identityUpdates.firstName = identity.firstName;
          if (identity.lastName) identityUpdates.lastName = identity.lastName;
          if (identity.email) identityUpdates.email = identity.email;
          if (identity.phone) identityUpdates.phone = identity.phone;
          if (identity.country) identityUpdates.country = identity.country;
          if (identity.preferredLanguage) {
            if (
              identity.preferredLanguage === "en" ||
              identity.preferredLanguage === "es"
            ) {
              identityUpdates.preferredLanguage = identity.preferredLanguage;
            }
          }
        }

        identityUpdates.emailVerified = true;

        if (Object.keys(identityUpdates).length > 0) {
          updateIntakeData(identityUpdates);
        }

        setTrustedSessionStatus("ready");

        if (typeof window !== "undefined") {
          const params = new URLSearchParams(window.location.search);
          params.delete("token");
          const query = params.toString();
          const newUrl = query
            ? `${window.location.pathname}?${query}${window.location.hash}`
            : `${window.location.pathname}${window.location.hash}`;
          const prevState = window.history.state;
          window.history.replaceState(
            prevState && typeof prevState === "object" && !Array.isArray(prevState)
              ? { ...(prevState as Record<string, unknown>) }
              : {},
            "",
            newUrl,
          );
        }
      } catch (error) {
        if (cancelled) {
          return;
        }

        setTrustedSessionStatus("error");

        const message =
          error instanceof Error
            ? error.message
            : "This link has expired or has already been used.";

        setStepError({
          message,
          status:
            error instanceof Error
              ? Number((error as { status?: number }).status || 0) || null
              : null,
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [searchParams, trustedToken, trustedSource, updateIntakeData]);

  useEffect(() => {
    if (verifiedParam === "1" && queryFormUid) {
      updateIntakeData({
        formUid: queryFormUid,
        referenceUid: queryReferenceUid ?? undefined,
        emailVerified: true,
      });
      if (typeof window !== "undefined" && !isDesktopViewport()) {
        replaceMobileIntakeHistory(5);
      }
      setCurrentStep(5);
      setSearchParams({}, { replace: true });
    }
  }, [
    verifiedParam,
    queryFormUid,
    queryReferenceUid,
    updateIntakeData,
    setSearchParams,
    replaceMobileIntakeHistory,
  ]);

  const ensureDraftForm = useCallback(
    async (identityUpdates: Partial<IntakeData>) => {
      setStepError(null);
      const previousState = intakeDataRef.current;
      updateIntakeData(identityUpdates);

      const current = intakeDataRef.current;
      const existingFormUid = current.formUid;
      const email = normalizeEmail(current.email);
      if (!email) {
        throw new Error("Email is required to create your intake form.");
      }

      const draftEmail =
        draftEmailRef.current || normalizeEmail(previousState.email);
      if (existingFormUid) {
        if (draftEmail && draftEmail === email) {
          draftEmailRef.current = draftEmail;
          return;
        }

        if (draftEmail && draftEmail !== email) {
          clearDraftSyncTimeout(true);
          lastSyncedSignatureRef.current = null;
          draftEmailRef.current = null;
          updateIntakeData(buildEmailChangeReset(current));
        } else {
          draftEmailRef.current = email;
          return;
        }
      }

      try {
        const latest = intakeDataRef.current;
        const payload: IntakeData = {
          ...latest,
          email,
          uploads: latest.uploads || {},
          consentGiven: latest.consentGiven ?? false,
          emailVerified: latest.emailVerified ?? false,
        };

        const response = await patientIntakeApi.submitIntake(payload);
        const formUid = response.data?.formUid;
        const referenceUid = response.data?.referenceUid;

        if (!formUid) {
          throw new Error("Failed to create intake form.");
        }

        updateIntakeData({ formUid, referenceUid });
        draftEmailRef.current = email;
        clearDraftSyncTimeout(false);
        lastSyncedSignatureRef.current = JSON.stringify(
          buildFormPayload(intakeDataRef.current),
        );
        console.log("✅ Created new intake form draft:", formUid);
      } catch (creationError) {
        console.error("Error creating intake form:", creationError);
        const message =
          creationError instanceof Error
            ? creationError.message
            : "We could not create your intake form. Please try again.";
        const wrappedError = new Error(message);
        (wrappedError as { status?: number }).status =
          creationError instanceof Error
            ? Number((creationError as { status?: number }).status || 0) || undefined
            : undefined;
        throw wrappedError;
      }
    },
    [updateIntakeData, clearDraftSyncTimeout],
  );

  const nextStep = async () => {
    setStepError(null);
    if (currentStep < STEPS.length - 1) {
      const nextStepIndex = currentStep + 1;
      const nextStepData = STEPS[nextStepIndex];

      // Create form when transitioning to review step - after all data is collected
      // Check by step ID instead of index to handle different flow lengths
      if (nextStepData?.id === "review" && !intakeDataRef.current.formUid) {
        try {
          // Validate required fields before creating form
          if (!hasValidIdentity(intakeDataRef.current)) {
            throw new Error(
              "Please complete all required fields before proceeding.",
            );
          }

          const response = await patientIntakeApi.submitIntake(
            intakeDataRef.current,
          );
          const formUid = response.data?.formUid;
          const referenceUid = response.data?.referenceUid;

          if (formUid) {
            updateIntakeData({ formUid, referenceUid });
            draftEmailRef.current = normalizeEmail(intakeDataRef.current.email);
            clearDraftSyncTimeout(false);
            lastSyncedSignatureRef.current = JSON.stringify(
              buildFormPayload(intakeDataRef.current),
            );
          } else {
            throw new Error("Failed to create intake form");
          }
        } catch (error) {
          console.error("Error creating intake form:", error);
          const errorMessage =
            error instanceof Error
              ? error.message
              : "There was an error creating your intake form. Please try again.";
          setStepError({
            message: errorMessage,
            status:
              error instanceof Error
                ? Number((error as { status?: number }).status || 0) || null
                : null,
          });
          return; // Don't proceed to next step
        }
      }

      scrollToTop("auto");
      // Defer step change so Radix portals (Select, Popover, etc.) can unmount first and avoid removeChild errors
      if (typeof window !== "undefined") {
        window.requestAnimationFrame(() => {
          const prevIdx = currentStepRef.current;
          const nextIdx = Math.min(prevIdx + 1, stepsRef.current.length - 1);
          if (nextIdx > prevIdx && !isDesktopViewport()) {
            pushMobileIntakeHistory(nextIdx);
          }
          setCurrentStep(nextIdx);
          window.setTimeout(() => scrollToTop("smooth"), 50);
        });
      } else {
        setCurrentStep((prev) => Math.min(prev + 1, STEPS.length - 1));
      }
    }
  };

  const prevStep = () => {
    if (currentStep <= 0) {
      return;
    }
    if (typeof window !== "undefined" && !isDesktopViewport()) {
      window.history.back();
      return;
    }
    scrollToTop("auto");
    setCurrentStep((prev) => Math.max(prev - 1, 0));
    if (typeof window !== "undefined") {
      window.setTimeout(() => {
        scrollToTop("smooth");
      }, 50);
    }
  };

  const skipStep = () => {
    scrollToTop("auto");
    const prevIdx = currentStepRef.current;
    const newIndex = Math.min(prevIdx + 1, stepsRef.current.length - 1);
    if (newIndex > prevIdx && !isDesktopViewport()) {
      pushMobileIntakeHistory(newIndex);
    }
    setCurrentStep(newIndex);
    if (typeof window !== "undefined") {
      window.setTimeout(() => {
        scrollToTop("smooth");
      }, 50);
    }
  };

  const [retakePhotoContext, setRetakePhotoContext] = useState<{
    photoId: string;
    mediaId?: string;
  } | null>(null);

  const handleRetakePhoto = (payload: {
    photoId: string;
    mediaId?: string;
  }) => {
    setRetakePhotoContext(payload);
  };

  const handleRetakeComplete = () => {
    setRetakePhotoContext(null);
    const reviewIndex = STEPS.findIndex((step) => step.id === "review");
    if (reviewIndex !== -1 && !isDesktopViewport()) {
      replaceMobileIntakeHistory(reviewIndex);
    }
    setCurrentStep(reviewIndex);
  };

  const handleRetakeCancel = () => {
    setRetakePhotoContext(null);
  };

  const handleOTPVerificationSuccess = () => {
    // Redirect to jema-manager will be handled in the OTP component
    nextStep(); // Go to success step
  };

  const handleVerificationSuccess = () => {
    nextStep(); // Go to success step
  };

  const submitIntake = async () => {
    setStepError(null);
    setIsSubmitting(true);
    try {
      await flushDraftSync();

      const currentData = intakeDataRef.current;

      // Form is already created, just proceed to email verification
      if (currentData.formUid) {
        console.log(
          "✅ Finalizing intake form before verification",
          currentData.formUid,
        );
        await finalizeWithConsents(
          patientIntakeApi.finalizeIntake,
          currentData as IntakeData & { formUid: string },
        );
        console.log("✅ Form finalized, proceeding to next step");

        // Track referral completed (silently updates Supabase table status to 'intake_completed')
        void trackReferralCompleted(currentData.firstName?.trim() || "");

        if (trustedSessionStatus === "ready") {
          const successIndex = STEPS.findIndex((step) => step.id === "success");
          if (successIndex !== -1) {
            if (!isDesktopViewport()) {
              pushMobileIntakeHistory(successIndex);
            }
            setCurrentStep(successIndex);
          } else {
            nextStep();
          }
        } else {
          nextStep(); // Go to email verification step
        }
      } else {
        throw new Error("Form ID not found. Please try again.");
      }
    } catch (error) {
      console.error("Error submitting intake:", error);
      setStepError(
        {
          message:
            error instanceof Error
              ? error.message
              : "There was an error submitting your intake. Please try again.",
          status:
            error instanceof Error
              ? Number((error as { status?: number }).status || 0) || null
              : null,
        },
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const safeStepIndex = Math.min(currentStep, STEPS.length - 1);
  const currentStepData = STEPS[safeStepIndex];
  const currentStepTitle = currentStepData ? i18n._(currentStepData.title) : "";
  const trackableSteps = STEPS.slice(0, -1);
  const totalStepsToShow = trackableSteps.length;
  const lastTrackableIndex = Math.max(totalStepsToShow - 1, 0);
  const clampedStepIndex = Math.min(safeStepIndex, lastTrackableIndex);
  const progressFraction =
    lastTrackableIndex <= 0 ? 1 : clampedStepIndex / lastTrackableIndex;
  const progressPercent = Math.round(progressFraction * 100);
  const completedIntakeLabel = i18n._(msg`Completed Intake`);
  const activeStepTitle =
    safeStepIndex < totalStepsToShow ? currentStepTitle : completedIntakeLabel;

  const displayStepNumber = safeStepIndex + 1;
  const visibleStepNumber = Math.min(displayStepNumber, totalStepsToShow);
  const activeStepSubtitleDescriptor =
    STEP_SUBHEADINGS[currentStep] ??
    STEP_SUBHEADINGS[STEP_SUBHEADINGS.length - 1];
  const activeStepSubtitle = i18n._(activeStepSubtitleDescriptor);

  const skipVerificationStep = trustedSessionStatus === "ready";

  const dentalItems = [
    <MemoizedDynamicQuestionTree
      key="detailed"
      data={intakeData}
      onUpdate={updateIntakeData}
      onNext={nextStep}
      onBack={prevStep}
    />,
    <MemoizedPhotoCaptureStep
      key="photos"
      data={intakeData}
      onUpdate={updateIntakeData}
      onNext={nextStep}
      onBack={prevStep}
    />,
    <MemoizedAdditionalPhotosStep
      key="additional-photos"
      data={intakeData}
      onUpdate={updateIntakeData}
      onNext={nextStep}
      onBack={prevStep}
    />,
    <MemoizedComprehensiveDentalAnalysis
      key="analysis"
      data={intakeData}
      onUpdate={updateIntakeData}
      onNext={nextStep}
    />,
    <MemoizedReviewStep
      key="review"
      data={intakeData}
      onUpdate={updateIntakeData}
      onSubmit={submitIntake}
      isSubmitting={isSubmitting}
      onRetakePhoto={handleRetakePhoto}
      onBack={prevStep}
    />,
    ...(!skipVerificationStep
      ? [
          <MemoizedOTPVerificationStep
            key="verification"
            data={intakeData}
            onUpdate={updateIntakeData}
            onVerificationSuccess={handleOTPVerificationSuccess}
            onBack={prevStep}
            formUid={intakeData.formUid}
            referenceUid={intakeData.referenceUid}
          />,
        ]
      : []),

    <MemoizedSuccessStep key="success" data={intakeData} />,
  ];

  const procedureSlug =
    typeof intakeData.procedures === "string"
      ? intakeData.procedures
      : intakeData.procedures?.slug || "";

  const isDentistry = procedureSlug === "dentistry";

  const items = [
    <WelcomeStep
      data={intakeData}
      onUpdate={updateIntakeData}
      onEnsureDraft={ensureDraftForm}
      onNext={nextStep}
    />,
    <MedicalProcedureStep
      data={intakeData}
      onUpdate={updateIntakeData}
      onNext={nextStep}
      onBack={prevStep}
    />,
    ...(isDentistry
      ? dentalItems
      : [
          <DetailedQuestionsStep
            key="detailed"
            data={intakeData}
            onUpdate={updateIntakeData}
            onNext={nextStep}
            onBack={prevStep}
          />,
          <UploadsStep
            key="uploads"
            data={intakeData}
            onUpdate={updateIntakeData}
            onNext={nextStep}
            onBack={prevStep}
          />,
          <ReviewConsentStep
            key="review"
            data={intakeData}
            onUpdate={updateIntakeData}
            onSubmit={submitIntake}
            isSubmitting={isSubmitting}
            onBack={prevStep}
          />,
          ...(!skipVerificationStep
            ? [
                <VerifyEmailStep
                  key="verification"
                  data={intakeData}
                  onUpdate={updateIntakeData}
                  onVerificationSuccess={handleVerificationSuccess}
                  onBack={prevStep}
                  formUid={intakeData.formUid}
                  referenceUid={intakeData.referenceUid}
                />,
              ]
            : []),
          <SuccessStep key="success" data={intakeData} />,
        ]),
  ];

  if (retakePhotoContext) {
    return (
      <SinglePhotoRetake
        data={intakeData}
        photoId={retakePhotoContext.photoId}
        existingMediaId={retakePhotoContext.mediaId}
        photoTitle=""
        onUpdate={updateIntakeData}
        onComplete={handleRetakeComplete}
        onCancel={handleRetakeCancel}
      />
    );
  }

  const renderStep = () => {
    // Use direct array access to handle dynamic step changes
    if (currentStep >= 0 && currentStep < items.length) {
      return items[currentStep];
    }
    return null;
  };

  const stepContent = renderStep();

  return (
    <div className="min-h-screen bg-[#F5FAFF]">
      {isDesktop ? (
        <div className="flex h-screen bg-[#EEF7FC] overflow-hidden">
          <aside className="w-80 lg:w-96 flex-shrink-0 bg-[#166B93] text-white flex flex-col shadow-2xl h-full relative overflow-hidden">
            {/* Elegant glassmorphic overlay for depth */}
            <div className="absolute inset-0 bg-gradient-to-b from-white/5 to-transparent pointer-events-none" />

            <div className="flex-shrink-0 p-6 border-b border-white/10 relative z-10">
              <div className="flex flex-col items-center">
                <img
                  src="/jema services logo full.svg"
                  alt="Jema Services Logo"
                  className="object-contain brightness-0 invert"
                />
              </div>
              <div className="text-white">
                <h1 className="text-lg font-semibold text-white mb-1">
                  <Trans>Medical Care Without Borders</Trans>
                </h1>
                <h2 className="text-sm text-white/90 mb-4">
                  <Trans>Premium Medical Tourism in Colombia</Trans>
                </h2>
                <p className="text-xs text-white/80 leading-relaxed">
                  <Trans>
                    World-class medical care powered by expert physicians.
                    Personalized treatment plans and a journey worth taking.
                  </Trans>
                </p>
              </div>
            </div>
            {currentStep < STEPS.length - 1 ? (
              <nav className="flex-1 px-4 py-6 space-y-2 overflow-hidden">
                <h3 className="text-white/60 font-semibold text-[10px] mb-4 px-4 uppercase tracking-[0.2em]">
                  <Trans>Your Progress</Trans>
                </h3>
                <div className="space-y-2">
                  {STEPS.slice(0, -1).map((step, index) => {
                    const stepTitle = i18n._(step.title);
                    const StepIcon = step.icon;
                    const isActive = index === currentStep;
                    const isCompleted = index < currentStep;
                    return (
                      <div
                        key={step.id}
                        ref={(el) => {
                          if (el) stepRefs.current[index] = el;
                        }}
                        className={`flex items-center gap-4 p-3 mx-2 rounded-xl transition-all duration-300 cursor-default ${isActive ? "bg-white/10 shadow-[0_4px_20px_rgba(0,0,0,0.1)] ring-1 ring-white/20 backdrop-blur-sm" : isCompleted ? "bg-[#CEF12F]/15 ring-1 ring-[#CEF12F]/30 hover:bg-[#CEF12F]/20" : "text-white/40"}`}
                      >
                        <div
                          className={`w-9 h-9 rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0 transition-all duration-300 ${isActive ? "bg-white text-[#166B93] shadow-[0_0_15px_rgba(255,255,255,0.3)]" : isCompleted ? "bg-[#CEF12F] text-[#1a3a1a] shadow-[0_0_12px_rgba(206,241,47,0.5)]" : "bg-white/5 text-white/30"}`}
                        >
                          {isCompleted ? (
                            <CheckCircle className="w-5 h-5" />
                          ) : (
                            <StepIcon className="w-5 h-5" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div
                            className={`font-medium text-sm lg:text-base truncate transition-colors duration-300 ${isActive ? "text-white" : isCompleted ? "text-[#CEF12F]" : "text-white/40"}`}
                          >
                            {stepTitle}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </nav>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <CheckCircle className="w-16 h-16 mx-auto text-white mb-4" />
                  <p className="mt-4 text-lg">
                    <Trans>Thank you!</Trans>
                  </p>
                </div>
              </div>
            )}
            <div className="flex-shrink-0 p-4 border-t border-white/10 text-white/40 text-[10px] text-center uppercase tracking-wider relative z-10">
              <p>© 2026 Jema Services. All Rights Reserved.</p>
            </div>
          </aside>
          <div className="flex-1 flex flex-col min-h-0 h-full">
            <header className="flex-shrink-0 bg-white border-b border-gray-200">
              <div className="h-20 flex items-center justify-between px-6 lg:px-8">
                {currentStep < STEPS.length - 1 ? (
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-[#166B93]/10 text-[#166B93]">
                      <currentStepData.icon className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800 text-lg">
                        {currentStepTitle}
                      </h3>
                      <p className="text-sm text-gray-500">
                        <Trans id="desktop.progress">
                          Step {visibleStepNumber} of {totalStepsToShow}
                        </Trans>
                      </p>
                    </div>
                  </div>
                ) : (
                  <div>
                    <h3 className="font-semibold text-gray-800 text-lg">
                      {currentStepTitle}
                    </h3>
                  </div>
                )}

                <div className="flex items-center gap-3">
                  {currentStep > 0 && currentStep < STEPS.length - 1 && (
                    <Button
                      variant="outline"
                      size="lg"
                      onClick={prevStep}
                      className="flex items-center gap-2"
                    >
                      <ChevronLeft className="w-5 h-5" />
                      <Trans>Back</Trans>
                    </Button>
                  )}

                  {(currentStepData?.id === "detailed" ||
                    currentStepData?.id === "uploads" ||
                    currentStepData?.id === "additional-photos") && (
                    <Button
                      variant="outline"
                      size="lg"
                      onClick={skipStep}
                      className="flex items-center gap-2"
                    >
                      {i18n._(msg`Skip`)}
                      <ChevronRight className="w-5 h-5" />
                    </Button>
                  )}
                </div>
              </div>
            </header>
            <main
              className="flex-1 overflow-y-auto bg-white h-0"
              ref={contentRef}
            >
              <div className="max-w-[1150px] w-full mx-auto px-4 sm:px-6 lg:px-8 py-8" key={currentStep}>
                {stepError ? (
                  <ErrorCallout
                    message={stepError.message}
                    status={stepError.status}
                    className="mb-4"
                  />
                ) : null}
                {stepContent}
              </div>
            </main>
          </div>
        </div>
      ) : (
        <div className="ios-viewport-fix flex min-h-screen flex-col bg-[#F5FAFF]">
          {/* Stunning Floating "Dynamic Island" Progress Pill */}
          <div className="fixed top-2 left-4 right-4 z-50 transition-all duration-300 transform translate-y-0">
            <div className="bg-white/95 backdrop-blur-2xl shadow-[0_8px_32px_rgba(22,107,147,0.15)] ring-1 ring-[#166B93]/10 rounded-2xl overflow-hidden flex items-center p-1.5 h-16">
              
              {/* Left: Step Number Box */}
              <div className="bg-gradient-to-br from-[#166B93] to-[#105373] w-12 h-12 rounded-xl flex flex-col items-center justify-center text-white shadow-inner flex-shrink-0">
                <span className="text-[10px] font-semibold uppercase tracking-wider opacity-80 leading-none mb-0.5"><Trans>Step</Trans></span>
                <span className="text-xl font-bold leading-none">{Math.min(displayStepNumber, totalStepsToShow)}</span>
              </div>

              {/* Middle: Title & Progress Bar */}
              <div className="flex-1 min-w-0 px-3 flex flex-col justify-center">
                <h3 className="text-sm font-bold text-gray-800 truncate mb-1.5">
                  {activeStepTitle}
                </h3>
                
                {/* Segmented Progress Indicator */}
                <div className="flex items-center gap-1 w-full">
                  {trackableSteps.map((step, index) => {
                    const hasCompleted = safeStepIndex > index;
                    const isActive = !hasCompleted && clampedStepIndex === index;
                    
                    return (
                      <div 
                        key={step.id} 
                        className={`h-1.5 rounded-full flex-1 transition-all duration-500 ease-out ${
                          hasCompleted 
                            ? "bg-[#166B93]" 
                            : isActive 
                              ? "bg-[#166B93] opacity-60" 
                              : "bg-gray-200"
                        }`}
                      />
                    );
                  })}
                </div>
              </div>

              {/* Right: Percentage Badge */}
              <div className="flex-shrink-0 bg-[#F5FAFF] px-3 py-1.5 rounded-lg border border-[#166B93]/10">
                <span className="text-xs font-bold text-[#166B93]">{progressPercent}%</span>
              </div>
            </div>
          </div>

          {/* Main scrollable content */}
          <main
            className="safe-area-bottom flex-1 overflow-y-auto"
            ref={contentRef}
          >
            {/* Logo and Header Section - Scrollable */}
            <div className="bg-[#166B93] text-white pt-24 pb-12 px-5 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-b from-white/5 to-transparent pointer-events-none" />
              <div className="max-w-lg mx-auto relative z-10 flex flex-col items-center">
                {/* Logo */}
                <div className="mb-6 mt-4 flex justify-center">
                  <div className="bg-white backdrop-blur-sm rounded-3xl p-3 shadow-[0_8px_30px_rgb(0,0,0,0.12)]">
                    <img
                      src="/Icon_Jema.svg"
                      alt="Jema Services"
                      className="h-12 w-auto object-contain"
                    />
                  </div>
                </div>

                {/* Step Subtitle */}
                <div className="text-center bg-white/10 backdrop-blur-sm rounded-full px-5 py-2 ring-1 ring-white/10">
                  <p className="text-xs font-medium text-white/90 tracking-wide">{activeStepSubtitle}</p>
                </div>
              </div>
            </div>

            {/* Content Card container - relying on dynamic children padding */}
            <div className="px-4 -mt-6 sm:-mt-8 pb-32 relative z-20" key={currentStep}>
              <div className="w-full bg-white shadow-2xl shadow-[#166B93]/20 rounded-3xl overflow-hidden pb-12 pt-8 sm:pt-10">
                  {stepError ? (
                    <ErrorCallout
                      message={stepError.message}
                      status={stepError.status}
                      className="mx-6 mb-4"
                    />
                  ) : null}
                  {stepContent}
              </div>
            </div>
          </main>
        </div>
      )}
    </div>
  );
}
